import clsx from "clsx";

import { CANVAS_PAPERS } from "../../canvasPaper";
import { t } from "../../i18n";

import "./CanvasPaperPicker.scss";

import type { CanvasPaper } from "../../types";

export const getCanvasPaperLabel = (paper: CanvasPaper) => {
  switch (paper) {
    case "plain":
      return t("labels.canvasPaperPlain");
    case "grid":
      return t("labels.canvasPaperGrid");
    case "twoLines":
      return t("labels.canvasPaperTwoLines");
    case "threeLines":
      return t("labels.canvasPaperThreeLines");
  }
};

const PREVIEW_WIDTH = 36;
const PREVIEW_HEIGHT = 28;
/** preview counterpart of the 5 mm step of the on-canvas ruling */
const PREVIEW_STEP = 4;

const PaperPreview = ({ paper }: { paper: CanvasPaper }) => {
  const lines: React.ReactNode[] = [];

  if (paper === "grid") {
    for (let x = PREVIEW_STEP; x < PREVIEW_WIDTH; x += PREVIEW_STEP) {
      lines.push(
        <line
          key={`v${x}`}
          x1={x}
          x2={x}
          y1={0}
          y2={PREVIEW_HEIGHT}
          className="CanvasPaperPicker__grid"
        />,
      );
    }
    for (let y = PREVIEW_STEP; y < PREVIEW_HEIGHT; y += PREVIEW_STEP) {
      lines.push(
        <line
          key={`h${y}`}
          x1={0}
          x2={PREVIEW_WIDTH}
          y1={y}
          y2={y}
          className="CanvasPaperPicker__grid"
        />,
      );
    }
  } else if (paper !== "plain") {
    // same rhythm as the canvas: thin blue helper line, then one thick blue
    // line (two lines) or two thick red lines (three lines)
    const period = paper === "threeLines" ? 3 : 2;
    for (let index = 0; index * PREVIEW_STEP < PREVIEW_HEIGHT; index++) {
      const y = PREVIEW_STEP / 2 + index * PREVIEW_STEP;
      const isHelperLine = index % period === 0;
      lines.push(
        <line
          key={y}
          x1={0}
          x2={PREVIEW_WIDTH}
          y1={y}
          y2={y}
          className={
            isHelperLine
              ? "CanvasPaperPicker__helper"
              : paper === "threeLines"
              ? "CanvasPaperPicker__line"
              : "CanvasPaperPicker__blueLine"
          }
        />,
      );
    }
  }

  return (
    <svg
      viewBox={`0 0 ${PREVIEW_WIDTH} ${PREVIEW_HEIGHT}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      {lines}
    </svg>
  );
};

export const CanvasPaperPicker = ({
  value,
  onChange,
  compact = false,
}: {
  value: CanvasPaper;
  onChange: (paper: CanvasPaper) => void;
  /** smaller, island-styled variant for the canvas footer */
  compact?: boolean;
}) => {
  return (
    <div
      className={clsx("CanvasPaperPicker", {
        "CanvasPaperPicker--compact": compact,
      })}
      role="radiogroup"
      aria-label={t("labels.canvasPaper")}
      data-testid={compact ? "canvas-paper-switcher" : "canvas-paper-picker"}
    >
      {CANVAS_PAPERS.map((paper) => {
        const label = getCanvasPaperLabel(paper);
        const isActive = paper === value;
        return (
          <button
            key={paper}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={label}
            title={compact ? `${t("labels.canvasPaper")}: ${label}` : label}
            className={clsx("CanvasPaperPicker__option", {
              active: isActive,
            })}
            onClick={() => onChange(paper)}
            data-testid={`canvas-paper-${paper}`}
          >
            <PaperPreview paper={paper} />
          </button>
        );
      })}
    </div>
  );
};
